import { useQuery } from '@tanstack/react-query';
import { activitiesApi, goalsApi, recurrenceSchedulesApi, Activity } from '../../api/client';
import { useTeamStore } from '../../stores/teamStore';
import { LIST_REFETCH_INTERVAL_MS } from '../../constants';

/**
 * Fetches activities, goals, recurrence schedules, and template activities in parallel.
 * Returns raw (encrypted) data — decryption is handled by the screen component.
 */
export function useActivitiesList() {
  const activeTeamId = useTeamStore((s) => s.activeTeamId);
  const teamParams = activeTeamId ? { team_id: activeTeamId } : undefined;

  return useQuery({
    queryKey: ['activities', 'list-with-deps', activeTeamId ?? 'personal'],
    // Always refetch on screen mount: cached data renders immediately (stale-
    // while-revalidate), then the background refetch updates it. Without this,
    // stale cache can linger indefinitely on a healthy network.
    refetchOnMount: 'always',
    refetchOnReconnect: true,
    // Cross-device convergence: another device's change shows up here within
    // one refetch interval while the screen is visible, and at once when the
    // app or tab regains focus — regardless of the 2-minute staleTime.
    refetchOnWindowFocus: 'always',
    refetchInterval: LIST_REFETCH_INTERVAL_MS,
    refetchIntervalInBackground: false,
    queryFn: async () => {
      const [activitiesRes, goalsRes, schedulesRes] = await Promise.all([
        activitiesApi.list(teamParams),
        goalsApi.list(teamParams),
        recurrenceSchedulesApi.list(teamParams),
      ]);

      const activityList = activitiesRes.data;
      const scheduleList = schedulesRes.data;

      // Fetch template activities for schedules (they're excluded from list endpoint)
      const templateIds = scheduleList
        .map((s) => s.activity_template_id)
        .filter((id): id is string => id != null);
      const uniqueTemplateIds = [...new Set(templateIds)];
      const templates = await Promise.all(
        uniqueTemplateIds.map(async (id) => {
          try {
            const res = await activitiesApi.get(id);
            return res.data;
          } catch {
            return null;
          }
        })
      );
      const validTemplates = templates.filter((t): t is Activity => t != null);

      return {
        activities: activityList,
        goals: goalsRes.data,
        schedules: scheduleList,
        templateActivities: validTemplates,
      };
    },
  });
}
